<template>
  <div>
    <b-card>
      <b-row class="align-items-end">
        <b-col md="2">
          <b-form-group :label="$t('Year')">
            <v-select
              v-model="year"
              :options="years"
              :clearable="false"
              @input="load"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group :label="$t('Month')">
            <v-select
              v-model="month"
              :options="months"
              :reduce="option => option.id"
              :get-option-label="option => $langName(option.name)"
              :clearable="false"
              @input="load"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="7"
          class="text-md-right mb-1"
        >
          <b-button
            variant="success"
            class="mr-1"
            :disabled="!canApprove"
            @click="approveBatch"
          >
            <feather-icon
              icon="CheckCircleIcon"
              class="mr-50"
            />
            {{ $t('Approve monthly list') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="!canSend"
            @click="sendBatch"
          >
            <feather-icon
              icon="UploadCloudIcon"
              class="mr-50"
            />
            {{ $t('Send to UzAsbo') }}
          </b-button>
        </b-col>
      </b-row>

      <b-row v-if="monthly.total_organizations !== undefined">
        <b-col md="3">
          <b-card
            no-body
            class="border p-1 mb-0 text-center"
          >
            <h3 class="mb-0">
              {{ monthly.total_organizations }}
            </h3>
            <small class="text-muted">{{ $t('Total organizations') }}</small>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card
            no-body
            class="border p-1 mb-0 text-center"
          >
            <h3 class="mb-0 text-success">
              {{ monthly.sent_count }}
            </h3>
            <small class="text-muted">{{ $t('Sent') }}</small>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card
            no-body
            class="border p-1 mb-0 text-center"
          >
            <h3 class="mb-0 text-danger">
              {{ monthly.not_sent_count }}
            </h3>
            <small class="text-muted">{{ $t('Not sent') }}</small>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card
            no-body
            class="border p-1 mb-0 text-center"
          >
            <h3 class="mb-0">
              {{ batch.total_items || 0 }}
            </h3>
            <small class="text-muted">{{ $t('Researchers count') }}</small>
          </b-card>
        </b-col>
      </b-row>

      <b-alert
        v-if="batch.id"
        :variant="batchVariant"
        show
        class="p-1 mt-2 mb-0"
      >
        <small>
          {{ $t('Batch status') }}:
          <strong>{{ $langName(batchStatusName) }}</strong>
          <template v-if="batch.notified_at">
            · {{ $t('Signal sent') }}: {{ formatDate(batch.notified_at) }}
          </template>
          <template v-if="batch.fetched_at">
            · {{ $t('Fetched by UzAsbo') }}: {{ formatDate(batch.fetched_at) }}
            ({{ batch.fetch_count }})
          </template>
        </small>
      </b-alert>
    </b-card>

    <b-card no-body>
      <div class="m-2 d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          {{ $t('Organizations') }}
        </h5>
        <b-form-checkbox
          v-model="onlyNotSent"
          switch
        >
          {{ $t('Only not sent') }}
        </b-form-checkbox>
      </div>
      <b-table
        :items="filteredOrganizations"
        :fields="organizationColumns"
        :busy="isBusy"
        responsive
        show-empty
        :empty-text="$t('No matching records found')"
        primary-key="organization_id"
        class="position-relative"
      >
        <template #head()="data">
          {{ $t(data.label) }}
        </template>
        <template #cell(index)="data">
          {{ data.index + 1 }}
        </template>
        <template #cell(is_sent)="data">
          <b-badge :variant="data.value ? getStatus(data.item.status).variant : 'light-danger'">
            {{ data.value ? $langName(getStatus(data.item.status).name) : $t('Not sent') }}
          </b-badge>
        </template>
        <template #cell(approved_at)="data">
          {{ formatDate(data.value) }}
        </template>
        <template #cell(actions)="data">
          <feather-icon
            v-if="data.item.list_id"
            icon="EyeIcon"
            class="cursor-pointer"
            size="16"
            @click="$router.push({ name: 'uz-asbo-view', params: { id: data.item.list_id } })"
          />
        </template>
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner variant="primary" />
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BButton, BTable, BBadge, BAlert,
  BSpinner, BFormCheckbox,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import uzAsboModule from '@/store/uz-asbo'
import config, {
  MONTHS, UZASBO_BATCH_STATUSES, getMonthName, getUzAsboStatus, yearOptions,
} from './config'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BTable,
    BBadge,
    BAlert,
    BSpinner,
    BFormCheckbox,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    const now = new Date()
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      months: MONTHS,
      years: yearOptions(),
      batch: {},
      isBusy: false,
      onlyNotSent: false,
    }
  },
  computed: {
    monthly() {
      return this.$store.state.uzAsbo.monthly || {}
    },
    organizations() {
      return this.monthly.organizations || []
    },
    filteredOrganizations() {
      if (!this.onlyNotSent) return this.organizations
      return this.organizations.filter(item => !item.is_sent)
    },
    canApprove() {
      return this.monthly.sent_count > 0
        && (!this.batch.id || this.batch.status === UZASBO_BATCH_STATUSES.FORMING)
    },
    canSend() {
      return this.batch.status === UZASBO_BATCH_STATUSES.APPROVED
    },
    batchStatusName() {
      return {
        [UZASBO_BATCH_STATUSES.FORMING]: { uz: 'Shakllanmoqda', ru: 'Формируется', en: 'Forming' },
        [UZASBO_BATCH_STATUSES.APPROVED]: { uz: 'Tasdiqlangan', ru: 'Утверждён', en: 'Approved' },
        [UZASBO_BATCH_STATUSES.SENT_TO_UZASBO]: {
          uz: 'UzAsbo ga yuborilgan', ru: 'Отправлен в UzAsbo', en: 'Sent to UzAsbo',
        },
      }[this.batch.status] || { uz: '—', ru: '—', en: '—' }
    },
    batchVariant() {
      return {
        [UZASBO_BATCH_STATUSES.FORMING]: 'secondary',
        [UZASBO_BATCH_STATUSES.APPROVED]: 'success',
        [UZASBO_BATCH_STATUSES.SENT_TO_UZASBO]: 'primary',
      }[this.batch.status] || 'secondary'
    },
  },
  created() {
    this.load()
  },
  methods: {
    getMonthName,
    getStatus(status) {
      return getUzAsboStatus(status)
    },
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString()
    },
    load() {
      this.isBusy = true
      const params = { year: this.year, month: this.month }
      this.$store.dispatch('uzAsbo/monthlyOrganizations', params)
        .catch(error => this.$errorSwal(this, error))
        .finally(() => {
          this.isBusy = false
        })
      this.loadBatch()
    },
    loadBatch() {
      this.$store.dispatch('uzAsbo/batchList', { year: this.year, month: this.month })
        .then(response => {
          const results = response.data.results || response.data
          this.batch = results.find(
            item => item.year === this.year && item.month === this.month,
          ) || {}
        })
        .catch(() => {
          this.batch = {}
        })
    },
    approveBatch() {
      this.$removeSwal(this, { year: this.year, month: this.month }, 'uzAsbo/batchApprove', this.load, {
        title: 'Are you sure?',
        text: 'uz asbo batch approve hint',
        confirmButtonText: 'Approve',
        successTitle: 'Success',
        successText: 'Successfully completed',
        errorText: 'There was an error in successful execution',
      })
    },
    sendBatch() {
      this.$removeSwal(this, { year: this.year, month: this.month }, 'uzAsbo/batchSend', this.load, {
        title: 'Are you sure?',
        text: 'uz asbo send hint',
        confirmButtonText: 'Send',
        successTitle: 'Sent',
        successText: 'Successfully completed',
        errorText: 'There was an error in successful execution',
      })
    },
  },
  setup() {
    const MODULE_NAME = 'uzAsbo'
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, uzAsboModule)

    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    return {
      MODULE_NAME,
      ...config(),
    }
  },
}
</script>
